import React, {
  CSSProperties,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ToolTip from "../tooltip/ToolTip";
import { useHorizontalWheelPan } from "../../hooks/useHorizontalWheelPan";

/**
 * スプレッドシート風の高密度データテーブル。
 *
 * 既存のフォーム的なリスト表示と異なり、行間マージンを排して 1 行の高さを抑え、
 * 一覧性を最優先する。列の操作は Excel / スプレッドシートの慣習に合わせる:
 *
 *  - 列ヘッダー右端のハンドルをドラッグして列幅を変更できる
 *  - ハンドルをダブルクリックすると、その列の内容に合わせて幅を自動調整する
 *  - `onHeaderClick` を渡した列はヘッダークリック（ソート等）に反応する
 *
 * セルの中身（行）は `children` に `<tr><td>` で渡す。列数は `columns` と
 * 一致させること。データ取得・編集などのドメインロジックは持たない。
 *
 * セル内のコントロール（input / Select 等）は枠線なしで置き、編集中の
 * フィードバックはセル自体の青いリング（`td:focus-within`）で示す。枠を持つ
 * 共通コンポーネントは枠なしバリアントを使う（例: `ContentTagInput` の
 * `variant="bare"`）。
 *
 * 表の枠と横スクロール領域はこのコンポーネントが内包する。呼び出し元が個別に
 * `overflow-x: auto` のラッパーを作ると、スクロールバーの可視化やトラックパッド
 * の横パン対応が抜けて「スクロールできない表」が生まれ、ラッパーの余白によって
 * 画面ごとに表の位置がばらつくため。外枠は `frame`、スクロール領域に紐づく
 * 属性は `scrollAreaProps` で差し込む。`maxBodyHeight` を渡すと同じ領域が縦
 * にもスクロールし、列ヘッダーが上端に固定される。
 *
 * 行・セルに付けられる補助クラス:
 *  - `tr.table-row-muted`   … 行全体をダークグレーに沈める（完了行など。文字は白に反転）
 *  - `tr.table-row-dimmed`  … 行全体を薄いグレー背景にする（保留行など）
 *  - `td.table-cell-muted`  … セルをダークグレーで沈める（値を持たない入力不可セルなど）
 *  - `td.table-cell-visible`… ポップオーバーを持つセルのクリップ解除
 *  - `th.task-col-serial` / `td.task-col-serial`
 *      … 直列の列（左の完了を待つ）として、左隣との縦境界線を太く描く
 */
export interface TableColumn {
  /** 列の識別子（列幅の管理キー） */
  key: string;
  /** ヘッダーセルの内容 */
  header?: ReactNode;
  /** 初期の列幅(px)。省略した列は残り幅を吸収する（可変列） */
  width?: number;
  /** リサイズ・自動調整時の最小幅(px)。既定 56 */
  minWidth?: number;
  /** false にすると列幅変更ハンドルを表示しない。既定 true */
  resizable?: boolean;
  /** ヘッダークリック時のハンドラ（ソート等） */
  onHeaderClick?: () => void;
  /** ヘッダーの title 属性（ツールチップ） */
  headerTitle?: string;
  /**
   * `th` に付与する追加クラス名。スプレッドシート的な視覚装飾（例: 直列列の
   * 太い境界線）を呼び出し側から差し込むための拡張点。
   */
  headerClassName?: string;
  /**
   * `th` に付与する追加スタイル。列ごとの背景色（タスクボードの工程配色等）を
   * 呼び出し側から差し込むための拡張点。sticky ヘッダーが本文に重なるため、
   * 背景色は必ず**不透明**な色を渡すこと（C-5: 表の背景透過禁止）。
   */
  headerStyle?: CSSProperties;
  /**
   * ドラッグでの並び替え対象に含めるか。`true` の列同士は D&D で順序を入れ替え
   * られる。固定列（先頭・追加・操作列など）は省略 / false にして除外する。
   * 列順の永続化は親の `onColumnReorder` で行う。
   */
  sortable?: boolean;
}

/**
 * 表の外枠。`card` は 1px ボーダー + 角丸のカード、`bare` は枠なし。
 * どちらも内側 padding は持たず、表は枠いっぱいに広がる。
 */
export type TableFrame = "card" | "bare";

export interface TableProps {
  columns: TableColumn[];
  /** tbody に入る行（`<tr><td>`）。列数は columns と一致させる */
  children: ReactNode;
  /** テーブル全体の最小幅(px)。これを下回る幅では内蔵のスクロール領域が横スクロールする */
  minWidth?: number;
  /** スクロール領域（最外要素）に付与するクラス。 */
  className?: string;
  /** 外枠の見せ方。既定は `card`。 */
  frame?: TableFrame;
  /**
   * スクロール領域へ渡す追加属性。クリップボード操作を受けるための
   * `tabIndex` / `onCopy` / `onPaste` のように、**スクロール領域そのものに
   * 紐づく**ハンドラに限って使う（見た目の調整には使わない）。
   */
  scrollAreaProps?: HTMLAttributes<HTMLDivElement>;
  /**
   * 本文の表示高さの上限(px)。指定すると表の**中**が縦スクロールになり、列
   * ヘッダーは上端に貼り付く。行数が多い表で「見える範囲」だけを抑えたい
   * （行は全部描いたまま、読んでいる位置を見失わせたくない）ときに使う。
   * 省略時は従来どおり表の高さは中身なり＝ページ側でスクロールする。
   */
  maxBodyHeight?: number;
  /**
   * スクロール領域の DOM 参照。スクロール位置を呼び出し元から制御したい場合
   * （初期位置を最下部に置く等）に渡す。見た目の調整には使わない。
   */
  scrollAreaRef?: React.MutableRefObject<HTMLDivElement | null>;
  /**
   * `sortable: true` の列をドラッグで並び替えたときに呼ばれる。受け取った
   * `orderedKeys` は対象列のみの新しい並びで、固定列は含まない。親で `columns`
   * 配列を再構築し、API へ永続化することを想定。
   */
  // eslint-disable-next-line no-unused-vars
  onColumnReorder?: (orderedKeys: string[]) => void;
}

const DEFAULT_MIN_WIDTH = 56;
const AUTO_FIT_MAX_WIDTH = 560;
/** th / td の左右 padding(px)。自動幅調整の計算と CSS で共用する */
const CELL_H_PADDING = 8;

/**
 * 表を包む横スクロール領域。
 *
 * - **内側 padding は持たない**。表は枠いっぱいに広がる（呼び出し元が
 *   `c-group` 等で余白を作ると、表だけが内側に縮んで画面ごとに揃わなくなる）。
 * - 背景は不透過の白。セル（`td`）は行単位の状態色（hover / 完了行）を透かす
 *   ために透明のままなので、背後の要素が透けないようここで塗る。
 * - スクロールバーは常時表示にする。グローバル既定（`soremo_style.scss`）は
 *   hover 時のみ表示だが、表がはみ出したとき横スクロールできると分からない。
 *   標準プロパティ（`scrollbar-width` / `scrollbar-color`）が auto 以外だと
 *   Chrome は `::-webkit-scrollbar` を無視するため、`&&` で詳細度を上げて
 *   auto に戻してから WebKit スタイルを当てる。
 */
const ScrollArea = styled.div<{ $frame: TableFrame; $maxHeight?: number }>`
  width: 100%;
  padding: 0;
  overflow-x: auto;
  background: #fff;

  ${({ $frame }) =>
    $frame === "card" &&
    css`
      border: 1px solid var(--soremo-border);
      border-radius: 4px;
    `}

  ${({ $maxHeight }) =>
    $maxHeight != null &&
    css`
      max-height: ${$maxHeight}px;
      overflow-y: auto;
    `}

  /* scrollAreaProps で tabIndex を渡した表（クリップボード操作を受けるもの）は
     フォーカス可能になる。マウス操作では枠を出さず、キーボード操作のときだけ
     フォーカス位置が分かるようにする。 */
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--soremo-blue);
    outline-offset: -2px;
  }

  && {
    scrollbar-width: auto;
    scrollbar-color: auto;
  }
  &&::-webkit-scrollbar {
    display: block;
    height: 8px;
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: var(--soremo-background);
  }
  &::-webkit-scrollbar-thumb {
    background: var(--soremo-light-gray);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: var(--soremo-gray);
  }
`;

const StyledTable = styled.table<{ $stickyHeader: boolean }>`
  border-collapse: collapse;

  /* 表の中を縦スクロールさせるときは列ラベルを上端に貼り付ける。
     border-collapse: collapse の下では sticky にした要素の border だけが
     一緒にスクロールして消えるため、下端の罫線は box-shadow で描く。 */
  ${({ $stickyHeader }) =>
    $stickyHeader &&
    css`
      thead th {
        position: sticky;
        top: 0;
        /* リサイズハンドル(z-index:2)より上。セル内容の上にも重ねる */
        z-index: 3;
        box-shadow: inset 0 -1px 0 var(--soremo-border);
      }
    `}

  th,
  td {
    padding: 4px ${CELL_H_PADDING}px;
    text-align: left;
    vertical-align: middle;
  }

  th {
    position: relative;
    /* リサイズハンドルを列境界に重ねるため、はみ出しを許可する */
    overflow: visible;
    border-bottom: 1px solid var(--soremo-border);
    /* 列ラベル行はスプレッドシート風に淡いグレーの帯にして、視線を表本文に集中させる */
    background: var(--soremo-border);
    color: #000;
  }

  td {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* セル内にポップオーバー（Select 等）を持つ場合、td 側でこのクラスを指定して
   * クリップを解除する。中身のはみ出し制御はセル内のコントロールに任せる。 */
  td.table-cell-visible {
    overflow: visible;
  }

  /* スプレッドシート風のグリッド線（外周は親の枠に任せる） */
  th + th,
  td + td {
    border-left: 1px solid var(--soremo-border);
  }

  tbody tr + tr {
    border-top: 1px solid var(--soremo-border);
  }

  tbody tr:hover {
    background-color: var(--soremo-background, #fafafa);
  }

  /* 編集中（フォーカス中）のセルはスプレッドシート同様、セル自体を青いリングで囲む */
  td:focus-within {
    box-shadow: inset 0 0 0 2px var(--soremo-blue);
  }

  /* 入力不可セルはダークグレーで完全に沈める（値も置かない用途） */
  td.table-cell-muted {
    background-color: var(--soremo-gray, #53565a);
  }

  /* 完了セル（チェックボックスが全てチェック済み）を弱いグレーで沈める。
   * hover より後に定義して優先させる。 */
  td.table-cell-dimmed {
    background-color: var(--soremo-border, #f0f0f0);
    color: var(--soremo-light-gray);
  }

  /* 直列列の境界線強調。先頭以外の「直列（左の完了を待つ）」列に付与し、
   * 左隣との縦境界線を太くして区切りを視覚的に強める（鍵アイコンの代替）。 */
  th.task-col-serial,
  td.task-col-serial {
    border-left: 3px solid var(--soremo-light-gray);
  }

  /* タスクボード: 本文に「本日」の日付を含む**セル**の未チェックチェックボックスを、
   * 青で目立たせる（完了行は task-row-muted 側で全色を白に揃える）。
   * 担当者ごとの配色（--task-checkbox-color。MarkdownContent が行単位で埋め込む）
   * がある場合はそちらを優先する。 */
  td.task-cell-due-today .task-list-item-checkbox:not(:checked)::before {
    color: var(--task-checkbox-color, var(--soremo-blue));
  }

  /* 弱いグレーアウト行（保留行など）。hover より後に定義して優先させる */
  tbody tr.table-row-dimmed {
    background-color: var(--soremo-border, #f0f0f0);
  }

  /* 強いグレーアウト行（完了行など）。ダーク背景のため文字色は白へ反転する。
   * 内部のセル個別スタイル（table-cell-dimmed / table-cell-muted）や hover の
   * 背景上書きより優先させ、行全体が一様に --soremo-gray で塗られるようにする。 */
  tbody tr.table-row-muted,
  tbody tr.table-row-muted:hover,
  tbody tr.table-row-muted td,
  tbody tr.table-row-muted td.table-cell-dimmed,
  tbody tr.table-row-muted td.table-cell-muted,
  tbody tr.table-row-muted td.table-cell-visible {
    background-color: var(--soremo-gray, #53565a);
    color: #fff;
  }
`;

const HeaderButton = styled.button.attrs({
  type: "button",
  className: "bodytext-13",
})`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  /* ソート可能ヘッダーのラベルは黒文字、ホバー時のフィードバックは青で示す */
  color: #000;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  user-select: none;
  /* bodytext-13 の line-height 200% はヘッダーでは間延びするため抑える */
  line-height: 150%;

  &:hover {
    color: var(--soremo-blue);
  }
`;

const HeaderLabel = styled.span.attrs({ className: "bodytext-13" })`
  display: flex;
  align-items: center;
  gap: 4px;
  /* テキストは黒で読みやすく、アイコン（material-symbols 等）は各コンポーネント側で
   * 明示的に色を持つため、ここでテキスト色を黒にしてもアイコン色には波及しない。 */
  color: #000;
  overflow: hidden;
  white-space: nowrap;
  line-height: 150%;
`;

const SortableTh = styled.th<{ $isDragging: boolean }>`
  cursor: ${({ $isDragging }) => ($isDragging ? "grabbing" : "grab")};
  opacity: ${({ $isDragging }) => ($isDragging ? 0.6 : 1)};
`;

const ResizeHandle = styled.span<{ $last: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  /* 最終列はテーブル外へはみ出すと横スクロールを誘発するため内側に寄せる */
  right: ${({ $last }) => ($last ? "0" : "-5px")};
  width: 9px;
  cursor: col-resize;
  z-index: 2;
  touch-action: none;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 4px;
    width: 2px;
    background: transparent;
  }

  &:hover::after,
  &:active::after {
    background: var(--soremo-blue);
  }
`;

/**
 * セル内容の自然幅（折り返しなしで必要な幅）を測る。
 * セルを複製して max-content の計測用コンテナに入れ、実際のフォント・
 * スタイルのままレイアウトさせた結果の幅を返す。
 */
const measureCellsMaxWidth = (
  cells: HTMLElement[],
  host: HTMLElement,
): number => {
  const measurer = document.createElement("div");
  measurer.style.cssText =
    "position:absolute;left:-99999px;top:0;visibility:hidden;white-space:nowrap;";
  host.appendChild(measurer);
  let max = 0;
  for (const cell of cells) {
    const probe = cell.cloneNode(true) as HTMLElement;
    probe.style.cssText =
      "display:inline-block;width:max-content;max-width:none;white-space:nowrap;padding:0;margin:0;border:none;";
    measurer.replaceChildren(probe);
    max = Math.max(max, probe.getBoundingClientRect().width);
  }
  measurer.remove();
  return max;
};

/**
 * `useSortable` を組み込んだ並び替え可能な `<th>`。リサイズハンドルは listener を
 * 受け取らないため、列幅変更とドラッグが両立する。
 */
interface SortableHeaderCellProps {
  columnKey: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
  children: ReactNode;
}

const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({
  columnKey,
  className,
  style: baseStyle,
  title,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: columnKey });

  const style: CSSProperties = {
    ...baseStyle,
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ToolTip description={title ?? ""}>
      <SortableTh
        ref={setNodeRef}
        className={className}
        style={style}
        $isDragging={isDragging}
        {...attributes}
        {...listeners}
      >
        {children}
      </SortableTh>
    </ToolTip>
  );
};

const Table: React.FC<TableProps> = ({
  columns,
  children,
  minWidth,
  className,
  frame = "card",
  scrollAreaProps,
  maxBodyHeight,
  scrollAreaRef,
  onColumnReorder,
}) => {
  const tableRef = useRef<HTMLTableElement>(null);
  // トラックパッドの横スワイプを、縦スクロールする祖先に取られずこの領域へ届ける
  const wheelPanRef = useHorizontalWheelPan<HTMLDivElement>();
  // 横パン用の ref callback と、呼び出し元へ渡す ref を同じ要素に束ねる
  const setScrollArea = useCallback(
    (el: HTMLDivElement | null) => {
      wheelPanRef(el);
      if (scrollAreaRef) scrollAreaRef.current = el;
    },
    [wheelPanRef, scrollAreaRef],
  );
  // リサイズ操作が入るまでは null（width:100% の流動レイアウト）。
  // 一度でも列幅を操作したら全列を px に固定し、Excel 同様の挙動にする。
  const [widths, setWidths] = useState<Record<string, number> | null>(null);
  const endDragRef = useRef<(() => void) | null>(null);

  // アンマウント時にドラッグ中の document リスナーを残さない
  useEffect(() => () => endDragRef.current?.(), []);

  /** 現在の描画幅をスナップショットして全列を px 固定にする */
  const freezeWidths = (): Record<string, number> => {
    if (widths) return widths;
    const map: Record<string, number> = {};
    const ths =
      tableRef.current?.querySelectorAll<HTMLTableCellElement>("thead th");
    columns.forEach((col, i) => {
      const measured = ths?.[i]?.getBoundingClientRect().width;
      map[col.key] = Math.round(measured || col.width || DEFAULT_MIN_WIDTH);
    });
    setWidths(map);
    return map;
  };

  const handleResizeStart = (col: TableColumn) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const frozen = freezeWidths();
    const startX = e.clientX;
    const startWidth = frozen[col.key] ?? col.width ?? DEFAULT_MIN_WIDTH;
    const minW = col.minWidth ?? DEFAULT_MIN_WIDTH;

    const onMove = (ev: MouseEvent) => {
      const next = Math.max(minW, Math.round(startWidth + ev.clientX - startX));
      setWidths((prev) => ({ ...(prev ?? frozen), [col.key]: next }));
    };
    const onUp = () => endDragRef.current?.();

    endDragRef.current = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      endDragRef.current = null;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  /** ダブルクリックで列幅を内容に合わせて自動調整する */
  const autoFitColumn = (col: TableColumn) => {
    const table = tableRef.current;
    const index = columns.findIndex((c) => c.key === col.key);
    if (!table || index < 0) return;

    const cells: HTMLElement[] = [];
    const headerLabel = table.querySelector<HTMLElement>(
      `thead th:nth-child(${index + 1}) [data-header-label]`,
    );
    if (headerLabel) cells.push(headerLabel);
    table
      .querySelectorAll<HTMLTableCellElement>(
        `tbody td:nth-child(${index + 1})`,
      )
      .forEach((td) => {
        // colspan セル（空状態・行追加など）は列幅の対象にしない
        if (!td.hasAttribute("colspan")) cells.push(td);
      });

    const contentWidth = measureCellsMaxWidth(
      cells,
      table.parentElement ?? document.body,
    );
    if (contentWidth <= 0) return;

    const frozen = freezeWidths();
    const minW = col.minWidth ?? DEFAULT_MIN_WIDTH;
    const next = Math.min(
      Math.max(Math.ceil(contentWidth) + CELL_H_PADDING * 2 + 2, minW),
      AUTO_FIT_MAX_WIDTH,
    );
    setWidths({ ...frozen, [col.key]: next });
  };

  const tableStyle: CSSProperties = widths
    ? {
        tableLayout: "fixed",
        width: columns.reduce(
          (sum, col) =>
            sum + (widths[col.key] ?? col.width ?? DEFAULT_MIN_WIDTH),
          0,
        ),
      }
    : { tableLayout: "fixed", width: "100%", minWidth };

  // 並び替え対象の列キー（順序保持）。`onColumnReorder` が無いか対象列が無い場合は
  // DnD コンテキストを噛ませない（既存テーブルへの影響を避ける）。
  const sortableKeys = columns
    .filter((col) => col.sortable)
    .map((col) => col.key);
  const dndEnabled = !!onColumnReorder && sortableKeys.length > 1;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (!onColumnReorder) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortableKeys.indexOf(String(active.id));
    const newIndex = sortableKeys.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onColumnReorder(arrayMove(sortableKeys, oldIndex, newIndex));
  };

  const renderHeaderInner = (col: TableColumn, index: number) => (
    <>
      {col.onHeaderClick ? (
        <HeaderButton data-header-label onClick={col.onHeaderClick}>
          {col.header}
        </HeaderButton>
      ) : (
        <HeaderLabel data-header-label>{col.header}</HeaderLabel>
      )}
      {col.resizable !== false && (
        <ToolTip description="ドラッグで列幅を変更 / ダブルクリックで自動調整">
          <ResizeHandle
            $last={index === columns.length - 1}
            data-resize-handle
            // ドラッグ列との競合を避けるため、リサイズハンドル上では pointer/mouse
            // イベントを止めて DnD センサーへ伝播させない。
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={handleResizeStart(col)}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              autoFitColumn(col);
            }}
          />
        </ToolTip>
      )}
    </>
  );

  const headerRow = (
    <tr>
      {columns.map((col, index) =>
        dndEnabled && col.sortable ? (
          <SortableHeaderCell
            key={col.key}
            columnKey={col.key}
            title={col.headerTitle}
            className={col.headerClassName}
            style={col.headerStyle}
          >
            {renderHeaderInner(col, index)}
          </SortableHeaderCell>
        ) : (
          <ToolTip key={col.key} description={col.headerTitle ?? ""}>
            <th className={col.headerClassName} style={col.headerStyle}>
              {renderHeaderInner(col, index)}
            </th>
          </ToolTip>
        ),
      )}
    </tr>
  );

  return (
    <ScrollArea
      ref={setScrollArea}
      className={className}
      $frame={frame}
      $maxHeight={maxBodyHeight}
      {...scrollAreaProps}
    >
      <StyledTable
        ref={tableRef}
        style={tableStyle}
        $stickyHeader={maxBodyHeight != null}
      >
        <colgroup>
          {columns.map((col) => (
            <col
              key={col.key}
              style={{ width: widths?.[col.key] ?? col.width }}
            />
          ))}
        </colgroup>
        {dndEnabled ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToHorizontalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableKeys}
              strategy={horizontalListSortingStrategy}
            >
              <thead>{headerRow}</thead>
            </SortableContext>
          </DndContext>
        ) : (
          <thead>{headerRow}</thead>
        )}
        <tbody>{children}</tbody>
      </StyledTable>
    </ScrollArea>
  );
};

export default Table;
